/* AR / Collections → Billing by Phase (Duncan 2026-10-16): "a list of the invoices that month by
   the job, but only showing what was being billed towards that trade". Like Invoice History's
   By month list, with a trade picked: each invoice in the period shows what it billed toward that
   trade (net of retainage), beside its full total, and the bottom line is the trade's billing for
   the period — the lighting team's number is L jobs in full plus the lighting lines on D jobs.
   The split comes from each AIA invoice's continuation sheet (GET /api/invoice-history/lines,
   read nightly from CE by the agent) and the words in each line (lib/trades). An invoice whose
   sheet hasn't been read yet can't be split; the page says how many there are.
   2026-10-19 (Duncan): the Job Costs look -- tiles across the top, one card per PROJECT (its
   invoices in the period added up) -- and clicking a project drops down "how it got to that billed
   number": each invoice and every line, tagged with its trade, the counted ones highlighted. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getInvoiceHistory, getInvoiceLines } from '../../api';
import LoadingState from '../LoadingState';
import StateScreen from '../ui/StateScreen';
import SegmentedControl from '../ui/SegmentedControl';
import { JcIcon } from '../jobcost/JobCostShared';
import { dateOf, fmtDay, money, periodKey, periodLabel, reversalKeys, statusMeta, stepPeriod, todayCentral } from '../../lib/invoiceHistory';
import { LIGHTING, TRADE_OPTIONS, splitInvoice, tradeLabelOf, tradeLines, wholeJobTrade } from '../../lib/trades';
import { matchesSearch, noMatchText } from '../../lib/pageSearch';
import KpiRow from './ArKpiRow';
import { MetricCol } from './ArCardParts';

const PERIODS = [
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
];
const card = { background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' };
// A project card's columns: the project, Billed to <trade>, the status, the chevron -- only the
// phase's billing (Duncan 2026-10-19: "I really only want to see the invoice to that particular
// phase"); the total row at the bottom uses the same columns.
const CARD_COLS = 'minmax(260px, 1fr) 210px 104px 22px';
const LINE_COLS = '44px minmax(0, 1fr) 150px 130px';
const iconBtn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--text-2)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer' };
const TONES = {
  success: { color: 'var(--success)', bg: 'var(--success-soft)' },
  blue: { color: 'var(--type-labor)', bg: 'color-mix(in oklch, var(--type-labor) 14%, transparent)' },
  danger: { color: 'var(--danger)', bg: 'var(--danger-soft)' },
  warning: { color: 'var(--warning)', bg: 'var(--warning-soft)' },
  neutral: { color: 'var(--text-3)', bg: 'var(--bg-surface-2)' },
};
const Pill = ({ tone, label, title }) => (
  <span title={title} style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', color: TONES[tone].color, background: TONES[tone].bg }}>{label}</span>
);
// Invoice History's select, so the filter bars look alike.
const Select = ({ value, onChange, options, label }) => (
  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} className="bbp-select"
      style={{ appearance: 'none', WebkitAppearance: 'none', height: 32, padding: '0 32px 0 12px', borderRadius: 8, minWidth: 150, fontSize: 14, fontWeight: 500, color: value ? 'var(--accent-text)' : 'var(--text-1)', fontFamily: 'var(--font-sans)', background: 'var(--bg-surface)', border: `1px solid ${value ? 'color-mix(in oklch, var(--accent) 45%, var(--border-default))' : 'var(--border-default)'}`, boxShadow: 'var(--shadow-xs)', cursor: 'pointer' }}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    <span style={{ position: 'absolute', right: 10, color: 'var(--text-3)', pointerEvents: 'none', display: 'inline-flex' }}><JcIcon name="chevron-d" size={14} /></span>
  </div>
);
const amountText = (v) => <span className="mono" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{money(v)}</span>;

// A line's trade: the picked trade's lines stand out; a trade from the job's name says so.
const TradeTag = ({ trade, counted, byName }) => (
  <span title={byName ? 'No trade word in this line, so the job’s name decides its trade' : undefined} style={{
    justifySelf: 'start', display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    padding: '1px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600,
    color: counted ? 'var(--accent-text)' : 'var(--text-3)', background: counted ? 'var(--accent-soft)' : 'transparent',
    border: `1px solid ${counted ? 'color-mix(in oklch, var(--accent) 24%, transparent)' : 'var(--border-default)'}`,
  }}>{tradeLabelOf(trade)}{byName ? ' · job name' : ''}</span>
);

/* Why an invoice can't be split yet, from its read (GET /api/invoice-history/lines readState) and its
   job's place on the Job Cost roster -- the reader reads roster jobs only (2026-10-18). */
const whyNotSplit = ({ j, key, freeform }, sheets) => {
  if (freeform) return { text: 'A freeform invoice the reader couldn’t print from CE yet' };
  const r = sheets && sheets.readState ? sheets.readState[key] : null;
  if (r && (r.status === 'queued' || r.status === 'running')) return { text: 'Being read now' };
  if (r && r.status === 'failed') return { text: `Read failed${r.error ? `: ${r.error}` : ''}`, warn: true };
  if (r && r.status === 'not_aia') return { text: 'Not an AIA invoice, so there are no lines to read', warn: true };
  if (r && r.status === 'no_document') return { text: 'No document in CE, so there are no lines to read' };
  if (r && r.status === 'read') return { text: 'Read, but its lines don’t add up to the invoice', warn: true };
  const roster = sheets && sheets.roster ? sheets.roster[String(j.job).toUpperCase()] : undefined;
  if (roster === 'archived') return { text: 'Not read: the job was archived before its invoices were read' };
  if (!roster || roster === 'deleted') return { text: 'Not read: the job isn’t on the Job Cost roster (the reader reads roster jobs only)' };
  return { text: 'Not read yet: the next read picks it up' };
};

const CANT_COLS = 'minmax(220px, 1.3fr) 150px 140px minmax(220px, 2fr)';

/* The invoices the page can't split, under the total: which, and why. */
const CantSplitList = ({ items, sheets, onOpenProject }) => (
  <section aria-label="Invoices that can't be split yet" style={{ ...card, overflow: 'hidden' }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '11px 18px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>
      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)' }}>Can&rsquo;t split yet</span>
      <span style={{ fontSize: 13, color: 'var(--text-3)' }}>{items.length} invoice{items.length === 1 ? '' : 's'} &middot; not in the totals above</span>
      <span style={{ flex: 1 }} />
      <span className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>{money(items.reduce((t, i) => t + i.inv.amount, 0))}</span>
    </div>
    <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: 760 }}>
        {items.map((it, k) => {
          const why = whyNotSplit(it, sheets);
          return (
            <div key={it.key} style={{ display: 'grid', gridTemplateColumns: CANT_COLS, gap: 14, alignItems: 'center', padding: '9px 18px', borderTop: k ? '1px solid var(--border-subtle)' : 'none', fontSize: 13.5, color: 'var(--text-2)' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.j.name}>{it.j.name}</div>
                {it.j.slug && onOpenProject
                  ? <button type="button" className="bbp-link mono" style={{ fontSize: 12.5, color: 'var(--text-3)' }} onClick={() => onOpenProject(it.j.slug)}>#{it.j.job} <JcIcon name="external" size={11} sw={1.7} /></button>
                  : <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)' }}>#{it.j.job}</span>}
              </div>
              <span><span className="mono" style={{ color: 'var(--text-1)' }}>{it.inv.number}</span> <span style={{ color: 'var(--text-3)' }}>&middot; {fmtDay(dateOf(it.inv))}</span></span>
              <span className="mono" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(it.inv.amount)}</span>
              <span style={{ fontSize: 13, color: why.warn ? 'var(--warning)' : 'var(--text-3)' }}>{why.text}</span>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// The note's list of what the box hid: each job once, its documents after it.
const hiddenText = (hidden) => {
  const byJob = new Map();
  hidden.forEach((h) => byJob.set(h.job, [...(byJob.get(h.job) || []), h.number]));
  return [...byJob].map(([job, numbers]) => `${job} ${numbers.join(' + ')}`).join(' · ');
};

/* One invoice in a project's drop-down: every line it billed this period with its trade, and what
   of it counts toward the picked trade -- the card's number is these footers added up. */
const InvoiceBreakdown = ({ j, inv, sheet, amount, trade, tradeLabel }) => {
  const whole = wholeJobTrade(j.job); // an L job (lighting) or an E job (Dev & Eng): the whole invoice
  const lines = whole ? [] : tradeLines(sheet, j.name);
  const shown = lines.filter((l) => Math.abs(l.net) > 0.004);
  const counted = shown.filter((l) => l.trade === trade);
  const quiet = lines.length - shown.length;
  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 10, boxShadow: 'var(--shadow-xs)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
        <span className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)' }}>Invoice {inv.number}</span>
        <span style={{ fontSize: 13, color: 'var(--text-3)' }}>{fmtDay(dateOf(inv))}{sheet && sheet.app ? ` · AFP ${sheet.app}` : sheet && !whole ? ' · freeform' : ''}</span>
        <Pill {...statusMeta(inv)} />
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: 'var(--text-3)' }}>Invoice total</span>
        <span className="mono" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>{money(inv.amount)}</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 560 }}>
          {whole ? (
            <div style={{ display: 'grid', gridTemplateColumns: LINE_COLS, gap: 10, alignItems: 'center', padding: '8px 14px', fontSize: 13.5 }}>
              <span />
              <span style={{ color: 'var(--text-1)' }}>An {String(j.job).match(/-([A-Z])-/i)[1].toUpperCase()} job: the whole invoice counts as {tradeLabelOf(whole)}</span>
              <TradeTag trade={whole} counted={trade === whole} />
              {amountText(inv.amount)}
            </div>
          ) : shown.map((l, i) => {
            const on = l.trade === trade;
            return (
              <div key={`${l.item}-${i}`} style={{ display: 'grid', gridTemplateColumns: LINE_COLS, gap: 10, alignItems: 'center', padding: '7px 14px', fontSize: 13.5, borderTop: i ? '1px solid var(--border-subtle)' : 'none', color: on ? 'var(--text-1)' : 'var(--text-3)' }}>
                <span className="mono" style={{ fontSize: 12.5 }}>{l.item}</span>
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: on ? 600 : 400 }} title={l.description}>{l.description || '(no description)'}</span>
                <TradeTag trade={l.trade} counted={on} byName={l.byName} />
                <span style={{ fontWeight: on ? 600 : 400 }}>{amountText(l.net)}</span>
              </div>
            );
          })}
          {quiet > 0 && (
            <div style={{ padding: '6px 14px', fontSize: 12.5, color: 'var(--text-4)', borderTop: '1px solid var(--border-subtle)' }}>
              {quiet} more line{quiet === 1 ? '' : 's'} billed nothing on this invoice.
            </div>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)', fontSize: 13.5 }}>
        <span style={{ fontWeight: 600, color: 'var(--text-1)' }}>Billed to {tradeLabel}</span>
        <span style={{ color: 'var(--text-3)' }}>
          {whole ? '· the whole invoice' : counted.length === 0 ? '· no lines' : counted.length <= 6 ? `· line${counted.length === 1 ? '' : 's'} ${counted.map((l) => l.item).join(', ')}` : `· ${counted.length} lines`}
        </span>
        <span style={{ flex: 1 }} />
        <span className="mono" style={{ fontWeight: 700, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>{money(amount)}</span>
      </div>
    </div>
  );
};

/* One project in the period: what it billed toward the trade, beside its invoices' full totals. A
   click drops down its invoices, line by line. */
const ProjectCard = ({ p, trade, tradeLabel, open, onToggle, onOpenProject, sheets }) => {
  const { j } = p;
  const whole = wholeJobTrade(j.job);
  const one = p.invoices.length === 1 ? p.invoices[0] : null;
  const onKey = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } };
  return (
    <article style={{ ...card, overflow: 'hidden' }}>
      <div className="bbp-cardhead" role="button" tabIndex={0} aria-expanded={open} onClick={onToggle} onKeyDown={onKey} style={{ cursor: 'pointer', padding: '15px 18px' }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ display: 'grid', gap: 22, alignItems: 'center', minWidth: 640, gridTemplateColumns: CARD_COLS }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
              <span style={{ fontSize: 16.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.012em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={j.name}>{j.name}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, fontSize: 13, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                {j.slug && onOpenProject ? (
                  <button type="button" className="bbp-link mono" title="Open the project" onClick={(e) => { e.stopPropagation(); onOpenProject(j.slug); }}>#{j.job} <JcIcon name="external" size={12} sw={1.7} /></button>
                ) : <span className="mono">#{j.job}</span>}
                {j.pm.length > 0 && <><span style={{ color: 'var(--text-4)' }}>·</span><span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>PM {j.pm.join(', ')}</span></>}
                {whole && <><span style={{ color: 'var(--text-4)' }}>·</span><span>{String(j.job).match(/-([A-Z])-/i)[1].toUpperCase()} job, all {tradeLabelOf(whole)}</span></>}
              </div>
            </div>
            <MetricCol big label={`Billed to ${tradeLabel}`} value={money(p.amount)} sub={`${p.invoices.length} invoice${p.invoices.length === 1 ? '' : 's'}`} />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{one ? <Pill {...statusMeta(one.inv)} /> : null}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', color: 'var(--text-3)' }}>
              <span style={{ display: 'inline-flex', transition: 'transform 180ms ease', transform: open ? 'rotate(180deg)' : 'none' }}><JcIcon name="chevron-d" size={18} /></span>
            </div>
          </div>
        </div>
      </div>
      {open && (
        <div style={{ background: 'var(--bg-surface-2)', borderTop: '1px solid var(--border-subtle)', padding: '14px 18px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.invoices.map(({ inv, amount, key }) => (
            <InvoiceBreakdown key={key} j={j} inv={inv} sheet={sheets && sheets.invoices[key]} amount={amount} trade={trade} tradeLabel={tradeLabel} />
          ))}
          {p.invoices.length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 14px 0', fontSize: 14 }}>
              <span style={{ fontWeight: 700, color: 'var(--text-1)' }}>{p.invoices.length} invoices · billed to {tradeLabel}</span>
              <span style={{ flex: 1 }} />
              <span className="mono" style={{ fontWeight: 800, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>{money(p.amount)}</span>
            </div>
          )}
        </div>
      )}
    </article>
  );
};

const BillingByPhaseView = ({ onOpenProject, search }) => {
  const [data, setData] = useState(null);
  const [sheets, setSheets] = useState(null); // { ready, invoices: { 'JOB|inv': { app, lines } } }
  const [error, setError] = useState(null);
  const [periodType, setPeriodType] = useState('month');
  const [pickedAt, setPickedAt] = useState(null);
  const [trade, setTrade] = useState(LIGHTING);
  const [pm, setPm] = useState('');
  const [cm, setCm] = useState('');
  const [sales, setSales] = useState('');
  // Ticked when the page opens (Duncan 2026-10-19): the billing that stands, without the credit
  // memos accounting posts to reverse an invoice, nor the invoices they reverse. Untick for the truth.
  const [hideReversals, setHideReversals] = useState(true);
  const [openJobs, setOpenJobs] = useState(() => new Set()); // the projects dropped down
  const toggleJob = (job) => setOpenJobs((prev) => {
    const next = new Set(prev);
    if (next.has(job)) next.delete(job); else next.add(job);
    return next;
  });

  const load = useCallback(() => {
    setError(null);
    Promise.all([getInvoiceHistory(), getInvoiceLines().catch(() => ({ ready: false, invoices: {} }))])
      .then(([d, l]) => { setData(d); setSheets(l || { ready: false, invoices: {} }); })
      .catch((e) => setError(e.message || 'Could not load the billing'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const jobs = useMemo(() => (data && data.jobs) || [], [data]);
  // Opens on the latest period with invoices, as Invoice History does.
  const bounds = useMemo(() => {
    const today = todayCentral();
    const dates = jobs.flatMap((j) => j.invoices.map(dateOf)).filter(Boolean).sort();
    return { first: dates[0] || today, last: today, latest: dates.filter((d) => d <= today).pop() || today };
  }, [jobs]);
  const periodAt = pickedAt || periodKey(bounds.latest, periodType);
  const choosePeriodType = (t) => { setPeriodType(t); setPickedAt(periodKey(bounds.latest, t)); };

  const options = useMemo(() => {
    const uniq = (field) => [...new Set(jobs.flatMap((j) => j[field]))].sort((a, b) => a.localeCompare(b));
    return {
      pm: [{ value: '', label: 'All PMs' }, ...uniq('pm').map((n) => ({ value: n, label: n }))],
      cm: [{ value: '', label: 'All CMs' }, ...uniq('cm').map((n) => ({ value: n, label: n }))],
      sales: [{ value: '', label: 'All sales people' }, ...uniq('sales').map((n) => ({ value: n, label: n }))],
    };
  }, [jobs]);

  // The period's invoices, each split by trade; the rows are the ones that billed the picked trade.
  const view = useMemo(() => {
    const rows = [];
    let notSplit = 0;
    let notSplitAmount = 0;
    // CE won't open a reversed invoice, nor the memo that cancels it (Invoice Lines, 2026-10-18):
    // both are left out of the split, and the note says so.
    const reversedKeys = new Set((sheets && sheets.reversed) || []);
    let reversed = 0;
    let reversedAmount = 0;
    // A freeform invoice (entered straight into AR) the AIA reader doesn't read: left out too, said so.
    const freeformKeys = new Set((sheets && sheets.freeform) || []);
    let freeform = 0;
    let freeformAmount = 0;
    // "Hide credit memos and reversals": every credit memo and each invoice one reverses leave the
    // view -- in whichever period each falls -- as do the reads CE answered "reversed"; the note
    // lists them. Hidden, not dropped: they stay in CE and in Summit.
    const hiddenKeys = hideReversals ? reversalKeys(jobs) : new Set();
    const hidden = [];
    const cantSplit = []; // listed under the total (Duncan 2026-10-19: "where are they?")
    jobs
      .filter((j) => (!pm || j.pm.includes(pm)) && (!cm || j.cm.includes(cm)) && (!sales || j.sales.includes(sales)))
      .forEach((j) => j.invoices.forEach((inv) => {
        if (periodKey(dateOf(inv), periodType) !== periodAt) return;
        // The top bar's search (ARPage holds it for this view; lib/pageSearch): the invoice's number
        // and status, its job's number, name and people. Before the split, so the not-split count follows it.
        if (!matchesSearch(search, j.job, j.name, j.pm, j.cm, j.sales, inv.number, statusMeta(inv).label)) return;
        const key = `${String(j.job).toUpperCase()}|${inv.number}`;
        if (hiddenKeys.has(key) || (hideReversals && reversedKeys.has(key))) { hidden.push({ job: j.job, number: inv.number, amount: inv.amount }); return; }
        const s = splitInvoice(j.job, inv, sheets && sheets.invoices[key], j.name);
        if (!s.split && reversedKeys.has(key)) { reversed += 1; reversedAmount += inv.amount; return; }
        if (!s.split && freeformKeys.has(key)) { freeform += 1; freeformAmount += inv.amount; cantSplit.push({ j, inv, key, freeform: true }); return; }
        if (!s.split) { notSplit += 1; notSplitAmount += inv.amount; cantSplit.push({ j, inv, key }); return; }
        const amount = s.byTrade[trade] || 0;
        if (Math.abs(amount) < 0.005) return;
        rows.push({ j, inv, amount, key });
      }));
    // One card per project (2026-10-19): its invoices in the period added up, newest invoice first.
    const byJob = new Map();
    rows.forEach((r) => {
      const p = byJob.get(r.j.job) || { j: r.j, invoices: [], amount: 0 };
      p.invoices.push(r);
      p.amount += r.amount;
      byJob.set(r.j.job, p);
    });
    const round2 = (n) => Math.round(n * 100) / 100;
    const projects = [...byJob.values()]
      .map((p) => ({ ...p, amount: round2(p.amount),
        invoices: p.invoices.sort((a, b) => String(dateOf(b.inv)).localeCompare(String(dateOf(a.inv)))) }))
      .sort((a, b) => b.amount - a.amount || a.j.name.localeCompare(b.j.name));
    return {
      projects,
      invoices: rows.length,
      notSplit,
      notSplitAmount,
      reversed,
      reversedAmount,
      freeform,
      freeformAmount,
      hidden,
      hiddenAmount: hidden.reduce((t, h) => t + h.amount, 0),
      cantSplit: cantSplit.sort((a, b) => String(dateOf(a.inv)).localeCompare(String(dateOf(b.inv))) || a.j.job.localeCompare(b.j.job)),
      total: rows.reduce((t, r) => t + r.amount, 0),
    };
  }, [jobs, sheets, periodType, periodAt, trade, pm, cm, sales, search, hideReversals]);

  if (error) return <StateScreen kind="error" title="Couldn't load the billing" body={error} primary={{ label: 'Try again', icon: 'refresh-cw', onClick: load }} />;
  if (!data) return <LoadingState message="Loading billing…" />;

  const tradeLabel = (TRADE_OPTIONS.find((t) => t.value === trade) || {}).label || 'trade';
  const firstKey = periodKey(bounds.first, periodType);
  const lastKey = periodKey(bounds.last, periodType);
  const anyPeople = pm || cm || sales;
  const period = periodLabel(periodAt, periodType);
  const cannot = view.notSplit + view.freeform;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <style>{`
        @media (min-width: 1290px) {
          .bbp-sticky { position: sticky; top: 56px; z-index: 6; }
        }
        .bbp-select:focus-visible, .bbp-step:focus-visible { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
        .bbp-step:disabled { opacity: .4; cursor: default; }
        .bbp-check { display: inline-flex; align-items: center; gap: 8px; height: 32px; padding: 0 12px; border-radius: 8px; font: 500 14px var(--font-sans);
          color: var(--text-1); background: var(--bg-surface); border: 1px solid var(--border-default); box-shadow: var(--shadow-xs); cursor: pointer; white-space: nowrap; user-select: none; }
        .bbp-check input { width: 15px; height: 15px; margin: 0; accent-color: var(--accent); cursor: pointer; }
        .bbp-check:has(input:focus-visible) { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
        .bbp-cardhead:hover { background: color-mix(in oklch, var(--bg-hover) 45%, transparent); }
        .bbp-cardhead:focus-visible { outline: none; box-shadow: inset 0 0 0 2px var(--accent-ring); }
        .bbp-link { background: none; border: none; padding: 0; font: inherit; color: inherit; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; }
        .bbp-link:hover { color: var(--accent-text); }
      `}</style>

      <section className="bbp-sticky" style={{ ...card, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <SegmentedControl value={periodType} onChange={choosePeriodType} options={PERIODS} />
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <button type="button" className="bbp-step" style={iconBtn} aria-label="Previous period" disabled={periodAt <= firstKey} onClick={() => setPickedAt(stepPeriod(periodAt, periodType, -1))}>‹</button>
          <span style={{ minWidth: 92, textAlign: 'center', fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>{periodLabel(periodAt, periodType)}</span>
          <button type="button" className="bbp-step" style={iconBtn} aria-label="Next period" disabled={periodAt >= lastKey} onClick={() => setPickedAt(stepPeriod(periodAt, periodType, 1))}>›</button>
        </div>
        <Select label="Trade" value={trade} onChange={setTrade} options={TRADE_OPTIONS} />
        <Select label="PM" value={pm} onChange={setPm} options={options.pm} />
        <Select label="CM" value={cm} onChange={setCm} options={options.cm} />
        <Select label="Sales person" value={sales} onChange={setSales} options={options.sales} />
        <label className="bbp-check" title="A reversal in CE is a credit memo against the invoice. Both leave this view; they stay in CE and in Summit.">
          <input type="checkbox" checked={hideReversals} onChange={(e) => setHideReversals(e.target.checked)} />
          Hide credit memos and reversals
        </label>
        {anyPeople && (
          <button type="button" onClick={() => { setPm(''); setCm(''); setSales(''); }} style={{ height: 32, padding: '0 11px', borderRadius: 8, fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer' }}>Clear</button>
        )}
        <div style={{ flex: 1, minWidth: 8 }} />
        <button type="button" onClick={load} style={iconBtn} title="Refresh" aria-label="Refresh billing"><JcIcon name="refresh" size={15} /></button>
      </section>

      <KpiRow label="Billing by phase totals" tiles={[
        { label: `Billed to ${tradeLabel}`, value: money(view.total), valueSize: 20, sub: `${period} · net of retainage`, muted: !view.projects.length },
        { label: 'Projects', value: String(view.projects.length), valueSize: 20, sub: `${view.invoices} invoice${view.invoices === 1 ? '' : 's'}`, muted: !view.projects.length },
        { label: 'Can’t split yet', value: String(cannot), valueSize: 20, valueColor: cannot ? 'var(--warning)' : undefined, sub: cannot ? `${money(view.notSplitAmount + view.freeformAmount)} not read yet` : 'every invoice read', muted: !cannot },
      ]} />

      {view.projects.length === 0 ? (
        <StateScreen kind="empty" title={`Nothing billed to ${tradeLabel} in ${period}`}
          body={view.notSplit ? `${view.notSplit} invoice${view.notSplit === 1 ? '' : 's'} in this period can't be split yet (their continuation sheets haven't been read).`
            : search && search.trim() ? noMatchText(search).body : 'Try another period, trade or filter.'} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {view.projects.map((p) => (
            <ProjectCard key={p.j.job} p={p} trade={trade} tradeLabel={tradeLabel} sheets={sheets}
              open={openJobs.has(p.j.job)} onToggle={() => toggleJob(p.j.job)} onOpenProject={onOpenProject} />
          ))}
          {/* The bottom line (Duncan 2026-10-19: "a total line item at the bottom of the list"), under the cards' figures. */}
          <section aria-label={`Total billed to ${tradeLabel}`} style={{ ...card, background: 'var(--bg-surface-2)', border: '1px solid var(--border-strong)', padding: '13px 18px' }}>
            <div style={{ overflowX: 'auto' }}>
              <div style={{ display: 'grid', gap: 22, alignItems: 'center', minWidth: 640, gridTemplateColumns: CARD_COLS }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)' }}>
                  Total · {view.projects.length} project{view.projects.length === 1 ? '' : 's'} · {view.invoices} invoice{view.invoices === 1 ? '' : 's'}
                </span>
                <span className="mono" style={{ textAlign: 'right', fontSize: 20, fontWeight: 800, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>{money(view.total)}</span>
                <span />
                <span />
              </div>
            </div>
          </section>
        </div>
      )}

      {view.cantSplit.length > 0 && <CantSplitList items={view.cantSplit} sheets={sheets} onOpenProject={onOpenProject} />}

      <div style={{ fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.5 }}>
        Net of retainage, by invoice date{data.as_of ? ` · AR data as of ${fmtDay(data.as_of)}` : ''}. L jobs count in full as lighting and E jobs as Dev &amp; Eng; on other jobs only the lines of each invoice&rsquo;s continuation sheet that bill toward {tradeLabel} (an engineering line is Dev &amp; Eng, unless it&rsquo;s for lighting work alone).
        {view.notSplit > 0 && (
          <span style={{ color: 'var(--warning)' }}> {view.notSplit} invoice{view.notSplit === 1 ? '' : 's'} ({money(view.notSplitAmount)}) in {period} can&rsquo;t be split yet: {sheets && sheets.ready ? 'their continuation sheets haven’t been read.' : 'the CE agent hasn’t started reading continuation sheets.'}</span>
        )}
        {view.hidden.length > 0 && (
          <span> Hidden in {period} (&ldquo;Hide credit memos and reversals&rdquo;): {hiddenText(view.hidden)} ({money(view.hiddenAmount)} net) &mdash; a reversal in CE is a credit memo against the invoice. Untick the box to count them.</span>
        )}
        {view.reversed > 0 && (
          <span> {view.reversed} reversed in CE ({money(view.reversedAmount)}) in {period} {view.reversed === 1 ? 'isn’t' : 'aren’t'} split: CE won&rsquo;t open a reversed invoice, and its credit memo cancels it.</span>
        )}
        {view.freeform > 0 && (
          <span> {view.freeform} freeform invoice{view.freeform === 1 ? '' : 's'} ({money(view.freeformAmount)}) in {period} {view.freeform === 1 ? 'isn’t' : 'aren’t'} split: freeform invoices aren&rsquo;t read by the AIA reader.</span>
        )}
      </div>
    </div>
  );
};

export default BillingByPhaseView;
